import React, { useId } from 'react';
import { Minus, Plus } from 'lucide-react';
import {
  type Nutrients, type NutrientKey,
  NUTRIENT_META, PORTION_STEPS, formatPortion,
} from '../../../utils/nutrition';

/**
 * "160 kcal · P 8 · C 27 · F 3" — every value the meal or entry has; an unknown says
 * "unknown" ("kcal unknown", "C unknown") in words, never 0.
 */
export function NutrientLine({ nutrients, keys = ['calories', 'protein', 'carbs', 'fat'], className = '' }: {
  nutrients: Partial<Nutrients>;
  keys?: NutrientKey[];
  className?: string;
}) {
  return (
    <span className={`tabular-nums ${className}`}>
      {keys.map((k, i) => {
        const v = typeof nutrients[k] === 'number' ? (nutrients[k] as number) : null;
        const m = NUTRIENT_META[k];
        return (
          <React.Fragment key={k}>
            {i > 0 && <span aria-hidden="true"> · </span>}
            {k === 'calories'
              ? (v === null ? <Unknown label="calories" shown="kcal unknown" /> : <>{v.toLocaleString()} kcal</>)
              : <>{m.short} {v === null ? <Unknown label={m.label.toLowerCase()} /> : v}</>}
          </React.Fragment>
        );
      })}
    </span>
  );
}

/**
 * ⭐ An unknown carries its WORD (owner, 2026-10-21 — the cinematic pass). It used to be an amber
 * "—", which on a saffron page read as decoration. The word is semibold in the line's own quiet
 * ink — the SAME ink as Today's hero "unknown"/"incomplete" (Honesty), the ring's and Meal plan's
 * "inc.": one rule for one fact on one page ("no red or amber on calories"; amber is kept for a
 * warning about what you DO, never for what a food doesn't know). Screen readers hear "calories unknown".
 */
export function Unknown({ label, shown = 'unknown' }: { label: string; shown?: string }) {
  return (
    <span data-unknown="" className="font-semibold text-muted-foreground">
      <span aria-hidden="true">{shown}</span>
      <span className="sr-only">{label} unknown</span>
    </span>
  );
}

/** − ¾ + : steps through the common portions; any positive number may be typed. */
export function PortionStepper({ value, onChange, label = 'Portion', id }: {
  value: number;
  onChange: (v: number) => void;
  label?: string;
  id?: string;
}) {
  const steps = PORTION_STEPS as readonly number[];
  const down = [...steps].reverse().find(s => s < value - 1e-9);
  const up = steps.find(s => s > value + 1e-9);
  const btn = 'inline-flex items-center justify-center w-9 h-9 sm:w-8 sm:h-8 rounded-lg border border-border text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed';
  return (
    <div className="inline-flex items-center gap-1.5" role="group" aria-label={label}>
      <button type="button" className={btn} onClick={() => down !== undefined && onChange(down)} disabled={down === undefined} aria-label={`Less (${down !== undefined ? formatPortion(down) : 'minimum'})`}>
        <Minus className="w-3.5 h-3.5" aria-hidden="true" />
      </button>
      <input
        id={id}
        type="number"
        inputMode="decimal"
        min={0.05}
        step={0.25}
        value={Number.isFinite(value) ? value : ''}
        onChange={e => { const n = Number(e.target.value); if (Number.isFinite(n) && n > 0) onChange(Math.round(n * 100) / 100); }}
        aria-label={`${label} in servings`}
        className="w-16 h-9 sm:h-8 rounded-lg border border-border bg-background px-2 text-center text-sm tabular-nums text-foreground"
      />
      <button type="button" className={btn} onClick={() => up !== undefined && onChange(up)} disabled={up === undefined} aria-label={`More (${up !== undefined ? formatPortion(up) : 'maximum'})`}>
        <Plus className="w-3.5 h-3.5" aria-hidden="true" />
      </button>
    </div>
  );
}

/** A labelled number field where blank means unknown (not 0). */
export function NutrientField({ k, value, onChange, idPrefix }: {
  k: NutrientKey;
  value: string;
  onChange: (v: string) => void;
  idPrefix: string;
}) {
  const m = NUTRIENT_META[k];
  const id = `${idPrefix}-${k}`;
  return (
    <label htmlFor={id} className="block">
      <span className="block text-xs font-medium text-muted-foreground mb-1">{m.label}{k === 'calories' ? '' : ` (${m.unit})`}</span>
      <input
        id={id}
        type="number"
        inputMode="decimal"
        min={0}
        step={k === 'calories' ? 1 : 0.1}
        value={value}
        placeholder="unknown"
        onChange={e => onChange(e.target.value)}
        className="w-full h-10 sm:h-9 rounded-lg border border-border bg-background px-2.5 text-sm tabular-nums text-foreground placeholder:text-muted-foreground/60"
      />
    </label>
  );
}

/** Inline error with a retry (failures are never silent). */
export function SaveError({ message, onRetry, onDismiss }: { message: string; onRetry?: () => void; onDismiss?: () => void }) {
  return (
    <div role="alert" className="flex flex-wrap items-center gap-2 rounded-lg border border-red-500/50 bg-red-500/5 px-3 py-2 text-xs text-red-600 dark:text-red-400">
      <span className="flex-1 min-w-0">{message}</span>
      {onRetry && <button type="button" onClick={onRetry} className="rounded-md border border-current px-2 py-1 font-semibold">Try again</button>}
      {onDismiss && <button type="button" onClick={onDismiss} className="rounded-md px-2 py-1 font-semibold opacity-80 hover:opacity-100">Dismiss</button>}
    </div>
  );
}

/** One answer from a few — the wizard's questions (a radio group of pill buttons). */
export function Choice<T extends string>({ name, value, options, onChange, color, stacked = false }: {
  name: string;
  value: T | null;
  options: readonly { value: T; label: string; hint?: string }[];
  onChange: (v: T) => void;
  color: string;
  stacked?: boolean;
}) {
  // A hint DESCRIBES its option — the option's name stays just its label.
  const idp = useId();
  return (
    <div role="radiogroup" aria-label={name} className={stacked ? 'grid grid-cols-1 sm:grid-cols-2 gap-2' : 'flex flex-wrap gap-2'}>
      {options.map(o => {
        const on = o.value === value;
        return (
          <button key={o.value} type="button" role="radio" aria-checked={on} onClick={() => onChange(o.value)}
            aria-describedby={o.hint ? `${idp}-${o.value}` : undefined}
            className={`min-h-[44px] sm:min-h-[40px] px-3 py-2 rounded-lg border text-sm text-left ${on ? 'font-semibold text-foreground' : 'border-border text-muted-foreground hover:text-foreground'}`}
            style={on ? { borderColor: color, backgroundColor: `${color}14` } : undefined}>
            <span>{o.label}</span>
            {o.hint && <span id={`${idp}-${o.value}`} aria-hidden="true" className="block text-[11px] font-normal text-muted-foreground">{o.hint}</span>}
          </button>
        );
      })}
    </div>
  );
}
